<template>
  <div>
    <!-- Filters Card -->
    <b-card class="mb-2">
      <b-row>
        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="الحالة"
            label-for="status-filter"
          >
            <v-select
              id="status-filter"
              v-model="filters.status"
              :options="statusOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="جميع الحالات"
              :clearable="true"
              @input="applyFilters"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="3"
        >
          <b-form-group
            label="الأولوية"
            label-for="priority-filter"
          >
            <v-select
              id="priority-filter"
              v-model="filters.priority"
              :options="priorityOptions"
              :reduce="option => option.value"
              label="label"
              placeholder="جميع الأولويات"
              :clearable="true"
              @input="applyFilters"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="2"
        >
          <b-form-group
            label="تاريخ الاستحقاق من"
            label-for="due-date-from"
          >
            <b-form-input
              id="due-date-from"
              v-model="filters.due_date_from"
              type="date"
              @change="applyFilters"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="2"
        >
          <b-form-group
            label="تاريخ الاستحقاق إلى"
            label-for="due-date-to"
          >
            <b-form-input
              id="due-date-to"
              v-model="filters.due_date_to"
              type="date"
              @change="applyFilters"
            />
          </b-form-group>
        </b-col>

        <b-col
          cols="12"
          md="2"
        >
          <b-form-group
            label="خيارات"
            label-for="overdue-only"
          >
            <b-form-checkbox
              id="overdue-only"
              v-model="filters.overdue_only"
              class="mt-1"
              @change="applyFilters"
            >
              المتأخرة فقط
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="resetFilters"
          >
            <feather-icon
              icon="XIcon"
              size="14"
              class="mr-50"
            />
            إعادة تعيين الفلاتر
          </b-button>
        </b-col>
      </b-row>
    </b-card>

    <!-- Tasks List -->
    <b-card>
      <div
        v-if="isLoadingTasks"
        class="text-center py-5"
      >
        <b-spinner variant="primary" />
        <p class="mt-2 text-muted">
          جاري تحميل المهام...
        </p>
      </div>

      <div
        v-else-if="tasks.length === 0"
        class="text-center py-5"
      >
        <feather-icon
          icon="CheckSquareIcon"
          size="48"
          class="text-muted mb-2"
        />
        <h5 class="text-muted">
          لم يتم العثور على مهام
        </h5>
        <p class="text-muted">
          ليس لديك أي مهام مسندة حتى الآن.
        </p>
      </div>

      <div v-else>
        <b-table
          :items="tasks"
          :fields="tableFields"
          responsive
          hover
          show-empty
          class="position-relative"
        >
          <!-- Title -->
          <template #cell(title)="data">
            <div>
              <h6 class="mb-0">
                {{ data.item.title }}
              </h6>
              <small class="text-muted">
                {{ data.item.description || 'لا يوجد وصف' }}
              </small>
            </div>
          </template>

          <!-- Project -->
          <template #cell(project)="data">
            <div v-if="hasProjectInfo(data.item)">
              <div class="font-weight-bold">
                {{ data.item.project_service.project.project_name }}
              </div>
              <small class="text-muted">
                {{ data.item.project_service.project.project_number }}
              </small>
            </div>
            <span
              v-else
              class="text-muted"
            >غير متوفر</span>
          </template>

          <!-- Status -->
          <template #cell(status)="data">
            <b-badge :variant="getStatusVariant(data.item.status)">
              {{ getStatusLabel(data.item.status) }}
            </b-badge>
          </template>

          <!-- Priority -->
          <template #cell(priority)="data">
            <b-badge :variant="getPriorityVariant(data.item.priority)">
              {{ getPriorityLabel(data.item.priority) }}
            </b-badge>
          </template>

          <!-- Progress -->
          <template #cell(completion_percentage)="data">
            <div class="d-flex align-items-center">
              <div
                class="progress flex-grow-1"
                style="height: 6px;"
              >
                <div
                  class="progress-bar"
                  :class="getProgressClass(data.item.completion_percentage)"
                  :style="{ width: data.item.completion_percentage + '%' }"
                />
              </div>
              <small class="ml-1 text-muted">
                {{ data.item.completion_percentage }}%
              </small>
            </div>
          </template>

          <!-- Due Date -->
          <template #cell(due_date)="data">
            <div v-if="data.item.due_date">
              <div :class="isOverdue(data.item.due_date, data.item.status) ? 'text-danger font-weight-bold' : ''">
                {{ formatDate(data.item.due_date) }}
              </div>
              <small
                v-if="isOverdue(data.item.due_date, data.item.status)"
                class="text-danger"
              >
                متأخر!
              </small>
            </div>
            <span
              v-else
              class="text-muted"
            >غير متوفر</span>
          </template>

          <!-- Estimated Hours -->
          <template #cell(estimated_hours)="data">
            {{ data.item.estimated_hours || 'غير متوفر' }}
          </template>
        </b-table>

        <!-- Pagination -->
        <b-row v-if="tasksPagination.last_page > 1">
          <b-col cols="12">
            <b-pagination
              v-model="currentPage"
              :total-rows="tasksPagination.total"
              :per-page="tasksPagination.per_page"
              align="center"
              @change="onPageChange"
            />
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BButton,
  BTable,
  BBadge,
  BSpinner,
  BPagination,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'MyTasks',
  components: {
    BCard,
    BRow,
    BCol,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BButton,
    BTable,
    BBadge,
    BSpinner,
    BPagination,
    vSelect,
  },
  data() {
    return {
      currentPage: 1,
      filters: {
        status: null,
        priority: null,
        due_date_from: null,
        due_date_to: null,
        overdue_only: false,
        per_page: 15,
      },
      statusOptions: [
        { label: 'قيد الانتظار', value: 'pending' },
        { label: 'قيد التنفيذ', value: 'in_progress' },
        { label: 'مكتملة', value: 'completed' },
        { label: 'ملغاة', value: 'cancelled' },
      ],
      priorityOptions: [
        { label: 'عاجلة', value: 'urgent' },
        { label: 'عالية', value: 'high' },
        { label: 'متوسطة', value: 'medium' },
        { label: 'منخفضة', value: 'low' },
      ],
      tableFields: [
        { key: 'title', label: 'المهمة', sortable: true },
        { key: 'project', label: 'المشروع', sortable: false },
        { key: 'status', label: 'الحالة', sortable: true },
        { key: 'priority', label: 'الأولوية', sortable: true },
        { key: 'completion_percentage', label: 'التقدم', sortable: true },
        { key: 'due_date', label: 'تاريخ الاستحقاق', sortable: true },
        { key: 'estimated_hours', label: 'الساعات المقدرة', sortable: true },
      ],
    }
  },
  computed: {
    ...mapGetters('profile', {
      tasks: 'tasks',
      tasksSummary: 'tasksSummary',
      tasksPagination: 'tasksPagination',
      isLoadingTasks: 'isLoadingTasks',
    }),
  },
  methods: {
    ...mapActions('profile', {
      fetchTasks: 'fetchTasks',
      filterTasks: 'filterTasks',
      resetTaskFilters: 'resetTaskFilters',
    }),

    applyFilters() {
      this.currentPage = 1
      this.filterTasks(this.filters)
    },

    async resetFilters() {
      this.filters = {
        status: null,
        priority: null,
        due_date_from: null,
        due_date_to: null,
        overdue_only: false,
        per_page: 15,
      }
      this.currentPage = 1
      await this.resetTaskFilters()
    },

    onPageChange(page) {
      const filtersWithPage = {
        ...this.filters,
        page,
      }
      this.filterTasks(filtersWithPage)
    },

    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        in_progress: 'info',
        completed: 'success',
        cancelled: 'secondary',
      }
      return variants[status] || 'secondary'
    },

    getStatusLabel(status) {
      const labels = {
        pending: 'قيد الانتظار',
        in_progress: 'قيد التنفيذ',
        completed: 'مكتملة',
        cancelled: 'ملغاة',
      }
      return labels[status] || status
    },

    getPriorityVariant(priority) {
      const variants = {
        urgent: 'danger',
        high: 'warning',
        medium: 'info',
        low: 'secondary',
      }
      return variants[priority] || 'secondary'
    },

    getPriorityLabel(priority) {
      const labels = {
        urgent: 'عاجلة',
        high: 'عالية',
        medium: 'متوسطة',
        low: 'منخفضة',
      }
      return labels[priority] || priority
    },

    getProgressClass(percentage) {
      if (percentage >= 75) return 'bg-success'
      if (percentage >= 50) return 'bg-info'
      if (percentage >= 25) return 'bg-warning'
      return 'bg-danger'
    },

    isOverdue(dueDate, status) {
      if (!dueDate || status === 'completed') return false
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      const due = new Date(dueDate)
      due.setHours(0, 0, 0, 0)
      return due < today
    },

    formatDate(date) {
      if (!date) return ''
      const d = new Date(date)
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    },

    hasProjectInfo(item) {
      return item && item.project_service && item.project_service.project
    },
  },
}
</script>

<style lang="scss" scoped>
.progress {
  background-color: #f3f2f7;
}
</style>
